import { auth } from '../config/firebase';
import {
  Slide,
  SlideFormData,
  SearchFilters,
  SearchResult,
  CreateSlideWithImagesRequest,
  SlideImage,
  PriceRecord,
  QuickAnalyzeResponse,
  TriggerAnalysisResponse,
  MarketplaceEvalResponse,
  BackgroundTask,
  RateLimitError,
  NamedCollection,
  ImportPreviewResponse,
  ImportStatusResponse,
  ImportHistoryItem,
  ImportDeletionPreview,
  ImportDeletionStatus,
  ImportWarning,
  ImportInProgressError
} from '../types';
import { localTodayISODate } from '../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_SLIDE_API_BASE_URL || 'https://slide.dev.carouselshopai.com';
// Image URLs: relative when deployed (same-origin via Firebase Hosting rewrite), absolute when running locally
const IMAGE_BASE_URL = process.env.REACT_APP_SLIDE_IMAGE_BASE_URL || '';

class SlideApiService {
  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.getAuthToken();
    const url = `${API_BASE_URL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();

      // Handle rate limit errors (HTTP 429)
      if (response.status === 429) {
        try {
          const errorBody = JSON.parse(errorText);
          if (errorBody.error === 'rate_limit_exceeded') {
            throw RateLimitError.fromResponse(errorBody);
          }
        } catch (parseError) {
          if (parseError instanceof RateLimitError) {
            throw parseError;
          }
        }
        throw new Error('Rate limit exceeded. Please try again later.');
      }

      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    if (response.status === 204 || response.status === 202) {
      return {} as T; // No content (202 Accepted for async share/un-share)
    }

    return response.json();
  }

  // Slide Management
  async getSlidesForShop(shopId: string, page: number = 0, size: number = 20): Promise<SearchResult<Slide>> {
    return this.request<SearchResult<Slide>>(`/api/v1/shops/${shopId}/slides?page=${page}&size=${size}`);
  }

  async getSlideById(shopId: string, id: string): Promise<Slide> {
    return this.request<Slide>(`/api/v1/shops/${shopId}/slides/${id}`);
  }

  /**
   * Batch fetch slides by IDs within a shop context.
   * Preserves input order (relevance ranking from intelligent search).
   * Missing or deleted IDs are silently omitted.
   */
  async batchGetSlides(shopId: string, slideIds: string[]): Promise<Slide[]> {
    return this.request<Slide[]>(`/api/v1/shops/${shopId}/slides/batch`, {
      method: 'POST',
      body: JSON.stringify({ slideIds }),
    });
  }

  // Create slide with primary image (multipart)
  async createSlideWithImages(request: CreateSlideWithImagesRequest, shopId?: string, autoAnalyze?: boolean, createAsActive?: boolean): Promise<Slide> {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error('User not authenticated');
    }

    const formData = new FormData();
    // Injected here rather than added to SlideFormData, which is shared with updateSlide where
    // the server ignores it. See localTodayISODate for why the client has to supply this.
    formData.append('slide', JSON.stringify({
      ...request.slide,
      acquisitionDate: localTodayISODate(),
    }));

    if (request.primaryImage) {
      formData.append('primaryImage', request.primaryImage);
    }

    if (request.primaryImageDescription) {
      formData.append('primaryImageDescription', request.primaryImageDescription);
    }

    // Append additional images if provided
    if (request.additionalImages && request.additionalImages.length > 0) {
      request.additionalImages.forEach((file) => {
        formData.append('additionalImages', file);
      });
    }

    // Append additional image descriptions if provided
    if (request.additionalImageDescriptions && request.additionalImageDescriptions.length > 0) {
      request.additionalImageDescriptions.forEach((desc) => {
        formData.append('additionalImageDescriptions', desc);
      });
    }

    // Use shop-specific endpoint
    if (!shopId) {
      throw new Error('shopId is required for slide creation');
    }
    const params = new URLSearchParams();
    if (autoAnalyze) params.append('autoAnalyze', 'true');
    if (createAsActive) params.append('createAsActive', 'true');
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const endpoint = `${API_BASE_URL}/api/v1/shops/${shopId}/slides${queryString}`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();

      // Handle rate limit errors (HTTP 429)
      if (response.status === 429) {
        try {
          const errorBody = JSON.parse(errorText);
          if (errorBody.error === 'rate_limit_exceeded') {
            throw RateLimitError.fromResponse(errorBody);
          }
        } catch (parseError) {
          if (parseError instanceof RateLimitError) {
            throw parseError;
          }
        }
        throw new Error('Rate limit exceeded. Please try again later.');
      }

      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  async updateSlide(shopId: string, id: string, slideData: SlideFormData): Promise<Slide> {
    return this.request<Slide>(`/api/v1/shops/${shopId}/slides/${id}`, {
      method: 'PUT',
      body: JSON.stringify(slideData),
    });
  }

  // Phase 3b: authoritatively set the slide's published flag and (full-replace) the set of
  // published image IDs. Mirrors the iOS setSlidePublishedV1 semantics:
  //   - `published` toggles the slide-level flag (server rejects with 400 if publishing a
  //     slide that would have 0 published images).
  //   - `publishedImageIds`, when provided, FULLY REPLACES the per-image published flags
  //     (any image not in the list is marked unpublished). Omit/undefined => leave image
  //     flags untouched. iOS always sends the full set, so we do too.
  async setSlidePublished(
    shopId: string,
    slideId: string,
    published: boolean,
    publishedImageIds?: string[]
  ): Promise<Slide> {
    return this.request<Slide>(`/api/v1/shops/${shopId}/slides/${slideId}/published`, {
      method: 'PATCH',
      body: JSON.stringify({
        published,
        ...(publishedImageIds !== undefined && { publishedImageIds }),
      }),
    });
  }

  async deleteSlide(shopId: string, id: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/slides/${id}`, {
      method: 'DELETE',
    });
  }

  async searchSlides(filters: SearchFilters): Promise<SearchResult<Slide>> {
    const params = new URLSearchParams();

    // Text search
    if (filters.nameQuery) params.append('nameQuery', filters.nameQuery);

    // Categories
    if (filters.categories && filters.categories.length > 0) {
      filters.categories.forEach(cat => params.append('categories', cat));
    }
    if (filters.categoriesOperator) params.append('categoriesOperator', filters.categoriesOperator);

    // Price filters
    if (filters.minPrice !== undefined) params.append('minPrice', filters.minPrice.toString());
    if (filters.maxPrice !== undefined) params.append('maxPrice', filters.maxPrice.toString());
    if (filters.priceType) params.append('priceType', filters.priceType);

    // Date filters
    if (filters.addedAfter) params.append('addedAfter', filters.addedAfter);
    if (filters.addedBefore) params.append('addedBefore', filters.addedBefore);

    // Image filters
    if (filters.imageCountMin !== undefined) params.append('imageCountMin', filters.imageCountMin.toString());
    if (filters.imageCountMax !== undefined) params.append('imageCountMax', filters.imageCountMax.toString());
    if (filters.hasPrimaryImage !== undefined) params.append('hasPrimaryImage', filters.hasPrimaryImage.toString());

    // Status filters
    if (filters.sold !== undefined) params.append('sold', filters.sold.toString());
    if (filters.hasListingPrice !== undefined) params.append('hasListingPrice', filters.hasListingPrice.toString());
    if (filters.available !== undefined) params.append('available', filters.available.toString());
    if (filters.active !== undefined) params.append('active', filters.active.toString());

    // Favorites filter (server short-circuits to the user's favorites; ignores other filters)
    if (filters.onlyFavorites) params.append('onlyFavorites', 'true');

    // Location/Place filters
    if (filters.locationId) params.append('locationId', filters.locationId);
    if (filters.placeId) params.append('placeId', filters.placeId);

    // Sorting and pagination
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.page !== undefined) params.append('page', filters.page.toString());
    if (filters.size !== undefined) params.append('size', filters.size.toString());

    const queryString = params.toString();

    // Use the new RESTful shop-scoped endpoint
    if (!filters.shopId) {
      throw new Error('shopId is required for slide search');
    }

    const endpoint = queryString
      ? `/api/v1/shops/${filters.shopId}/slides/search?${queryString}`
      : `/api/v1/shops/${filters.shopId}/slides/search`;

    return this.request<SearchResult<Slide>>(endpoint);
  }

  // Image Management
  async getSlideImages(shopId: string, slideId: string): Promise<SlideImage[]> {
    return this.request<SlideImage[]>(`/api/v1/shops/${shopId}/slides/${slideId}/images`);
  }

  async uploadImage(shopId: string, slideId: string, file: File, description?: string): Promise<SlideImage> {
    const token = await this.getAuthToken();

    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/shops/${shopId}/slides/${slideId}/image`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  async deleteImage(shopId: string, slideId: string, imageId: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/slides/${slideId}/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  async updateImage(shopId: string, slideId: string, imageId: string, updates: {
    description?: string;
    displayOrder?: number;
  }): Promise<SlideImage> {
    return this.request<SlideImage>(`/api/v1/shops/${shopId}/slides/${slideId}/images/${imageId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async reorderImages(shopId: string, slideId: string, imageIds: string[]): Promise<SlideImage[]> {
    return this.request<SlideImage[]>(`/api/v1/shops/${shopId}/slides/${slideId}/images/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ imageIds }),
    });
  }

  async rotateImage(shopId: string, slideId: string, imageId: string, degrees: number): Promise<SlideImage> {
    return this.request<SlideImage>(`/api/v1/shops/${shopId}/slides/${slideId}/images/${imageId}/rotate`, {
      method: 'POST',
      body: JSON.stringify({ degrees }),
    });
  }

  async cropImage(shopId: string, slideId: string, imageId: string, cropArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): Promise<SlideImage> {
    return this.request<SlideImage>(`/api/v1/shops/${shopId}/slides/${slideId}/images/${imageId}/crop`, {
      method: 'POST',
      body: JSON.stringify(cropArea),
    });
  }

  // Price Management (role-based access)
  async addPriceRecord(shopId: string, slideId: string, priceData: {
    type: 'ACQUISITION' | 'LISTING' | 'SOLD';
    amount: number;
    currency: string;
    notes?: string;
    effectiveDate?: string;
  }): Promise<PriceRecord> {
    return this.request<PriceRecord>(`/api/v1/shops/${shopId}/slides/${slideId}/price`, {
      method: 'POST',
      body: JSON.stringify(priceData),
    });
  }

  async deletePriceRecord(shopId: string, slideId: string, priceRecordId: string): Promise<void> {
    return this.request<void>(`/api/v1/shops/${shopId}/slides/${slideId}/price/${priceRecordId}`, {
      method: 'DELETE',
    });
  }

  async updatePriceRecord(shopId: string, slideId: string, priceRecordId: string, priceData: {
    type: 'ACQUISITION' | 'LISTING' | 'SOLD';
    amount: number;
    currency: string;
    notes?: string;
    effectiveDate?: string;
  }): Promise<PriceRecord> {
    return this.request<PriceRecord>(`/api/v1/shops/${shopId}/slides/${slideId}/price/${priceRecordId}`, {
      method: 'PUT',
      body: JSON.stringify(priceData),
    });
  }

  async getPriceHistory(shopId: string, slideId: string): Promise<PriceRecord[]> {
    return this.request<PriceRecord[]>(`/api/v1/shops/${shopId}/slides/${slideId}/price-history`);
  }

  async getCurrentPrice(shopId: string, slideId: string): Promise<PriceRecord | null> {
    try {
      return await this.request<PriceRecord>(`/api/v1/shops/${shopId}/slides/${slideId}/current-price`);
    } catch (error) {
      // 404 means no current price
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  // Favorites (per-user, per-shop)
  async favoriteSlide(shopId: string, slideId: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/slides/${slideId}/favorite`, {
      method: 'POST',
    });
  }

  async unfavoriteSlide(shopId: string, slideId: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/slides/${slideId}/favorite`, {
      method: 'DELETE',
    });
  }

  async getFavorites(shopId: string): Promise<{ slideIds: string[]; count: number }> {
    return this.request<{ slideIds: string[]; count: number }>(`/api/v1/shops/${shopId}/favorites`);
  }

  // Named Collections. Private collections are creator-only (others get 404); shop-visible ones
  // are open to internal roles per the server's access rule (403 when seen but not permitted).
  // from-favorites returns 400 when no favorites exist.
  //
  // scope 'all' = the caller's own collections plus other members' shop-visible ones. The server
  // defaults to 'mine' so app builds that predate shop-visible collections are unaffected.
  async listCollections(shopId: string, scope: 'mine' | 'all' = 'all'): Promise<NamedCollection[]> {
    return this.request<NamedCollection[]>(`/api/v1/shops/${shopId}/collections?scope=${scope}`);
  }

  /** Make a collection shop visible (true) or private again (false). Creator only on the server. */
  async setCollectionVisibility(shopId: string, collectionId: string, shopVisible: boolean): Promise<NamedCollection> {
    return this.request<NamedCollection>(`/api/v1/shops/${shopId}/collections/${collectionId}/visibility`, {
      method: 'PUT',
      body: JSON.stringify({ shopVisible }),
    });
  }

  async getCollection(shopId: string, collectionId: string): Promise<NamedCollection> {
    return this.request<NamedCollection>(`/api/v1/shops/${shopId}/collections/${collectionId}`);
  }

  async createCollection(shopId: string, name: string, slideRefs?: string[]): Promise<NamedCollection> {
    return this.request<NamedCollection>(`/api/v1/shops/${shopId}/collections`, {
      method: 'POST',
      body: JSON.stringify({ name, ...(slideRefs ? { slideRefs } : {}) }),
    });
  }

  async createCollectionFromFavorites(shopId: string, name: string): Promise<NamedCollection> {
    return this.request<NamedCollection>(`/api/v1/shops/${shopId}/collections/from-favorites`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async renameCollection(shopId: string, collectionId: string, name: string): Promise<NamedCollection> {
    return this.request<NamedCollection>(`/api/v1/shops/${shopId}/collections/${collectionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  }

  async addSlidesToCollection(shopId: string, collectionId: string, slideIds: string[]): Promise<NamedCollection> {
    return this.request<NamedCollection>(`/api/v1/shops/${shopId}/collections/${collectionId}/slides`, {
      method: 'POST',
      body: JSON.stringify({ slideIds }),
    });
  }

  async removeSlideFromCollection(shopId: string, collectionId: string, slideId: string): Promise<NamedCollection> {
    return this.request<NamedCollection>(`/api/v1/shops/${shopId}/collections/${collectionId}/slides/${slideId}`, {
      method: 'DELETE',
    });
  }

  async deleteCollection(shopId: string, collectionId: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/collections/${collectionId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Share a named collection to specific users and/or roles as a PUBLISHED_FOR_YOU feed cube (C1).
   * At least one target (userUids/roles) is required; every shared slide must already be published
   * (P6 pre-check surfaces unpublished ones before we get here). Returns 202 Accepted — the cube is
   * created asynchronously.
   */
  async shareCollection(
    shopId: string,
    collectionId: string,
    // expiresAt (any future ISO-8601 instant) OR noExpiry; neither = the server's default 4 days.
    body: { userUids?: string[]; roles?: string[]; title: string; reason?: string; expiresAt?: string; noExpiry?: boolean }
  ): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/collections/${collectionId}/share`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Stop sharing a named collection — expires its feed cube for the whole audience (C1b). 202
   * Accepted; idempotent (a collection that isn't currently shared is a harmless no-op).
   */
  async unshareCollection(shopId: string, collectionId: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/collections/${collectionId}/share`, {
      method: 'DELETE',
    });
  }

  // Category Management
  async getSlideCategories(shopId: string, slideId: string): Promise<string[]> {
    return this.request<string[]>(`/api/v1/shops/${shopId}/slides/${slideId}/categories`);
  }

  async addCategory(shopId: string, slideId: string, category: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/slides/${slideId}/categories?category=${encodeURIComponent(category)}`, {
      method: 'POST',
    });
  }

  async removeCategory(shopId: string, slideId: string, category: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/slides/${slideId}/categories?category=${encodeURIComponent(category)}`, {
      method: 'DELETE',
    });
  }

  async getShopCategories(shopId: string): Promise<string[]> {
    return this.request<string[]>(`/api/v1/shops/${shopId}/categories`);
  }

  // Category Autocomplete (Redis-backed prefix search)
  async getCategoryAutocomplete(shopId: string, prefix: string, limit: number = 10): Promise<string[]> {
    const params = new URLSearchParams();
    params.append('prefix', prefix);
    params.append('limit', limit.toString());
    const response = await this.request<{ categories: string[], count: number }>(`/api/v1/shops/${shopId}/categories/autocomplete?${params.toString()}`);
    return response.categories;
  }

  // Slide Status
  async getSlideStatus(shopId: string, slideId: string): Promise<{
    sold: boolean;
    hasListingPrice: boolean;
    imageCount: number;
    categoryCount: number;
    priceRecordCount: number;
  }> {
    return this.request(`/api/v1/shops/${shopId}/slides/${slideId}/status`);
  }

  // Active Property Permission Check
  // canModifyActive: may change the slide's `active` flag (PLATFORM_ADMIN/OWNER/ADMIN/MEMBER).
  // canPublish: may publish the slide or its images (PLATFORM_ADMIN/OWNER/ADMIN). Optional because a
  // server that predates the split omits it; treat missing as "same as canModifyActive" (the old rule).
  async canModifyActiveProperty(shopId: string, slideId: string): Promise<{ canModifyActive: boolean; canPublish?: boolean }> {
    return this.request(`/api/v1/shops/${shopId}/slides/${slideId}/can-modify-active`);
  }

  // Binary image data
  getImageUrl(shopId: string, slideId: string, imageId: string, size?: 'thumb_300' | 'thumb_800'): string {
    const sizeParam = size ? `?size=${size}` : '';
    return `${IMAGE_BASE_URL}/api/v1/shops/${shopId}/slides/${slideId}/images/${imageId}${sizeParam}`;
  }

  /**
   * Fetch authenticated image. Image IDs are immutable (content-addressed),
   * so the URL itself changes when image content changes. The server sends
   * Cache-Control: public, max-age=31536000, immutable for aggressive caching.
   */
  async fetchAuthenticatedImage(shopId: string, slideId: string, imageId: string, size?: 'thumb_300' | 'thumb_800'): Promise<Response> {
    const token = await this.getAuthToken();

    let url = `${IMAGE_BASE_URL}/api/v1/shops/${shopId}/slides/${slideId}/images/${imageId}`;
    if (size) {
      url += `?size=${size}`;
    }

    return fetch(url, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
  }

  // Fetch only headers (for HEIC detection without downloading full image)
  async fetchImageHeaders(shopId: string, slideId: string, imageId: string, size?: 'thumb_300' | 'thumb_800'): Promise<Response> {
    const token = await this.getAuthToken();

    // Build URL with new RESTful path structure
    let url = `${IMAGE_BASE_URL}/api/v1/shops/${shopId}/slides/${slideId}/images/${imageId}`;
    const params = new URLSearchParams();
    if (size) {
      params.append('size', size);
    }
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    return fetch(url, {
      method: 'HEAD',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
  }

  // Download full-size image
  async downloadImage(shopId: string, slideId: string, imageId: string, fileName?: string): Promise<void> {
    const token = await this.getAuthToken();

    const url = `${IMAGE_BASE_URL}/api/v1/shops/${shopId}/slides/${slideId}/images/${imageId}`;

    const response = await fetch(url, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download image: ${response.status}`);
    }

    // Get the blob and trigger download
    const blob = await response.blob();

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers.get('Content-Disposition');
    let downloadFileName = fileName;

    if (!downloadFileName && contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        downloadFileName = filenameMatch[1];
      }
    }

    // Fallback to imageId with extension from Content-Type
    if (!downloadFileName) {
      const contentType = response.headers.get('Content-Type');
      const extension = contentType?.split('/')[1] || 'jpg';
      downloadFileName = `${imageId}.${extension}`;
    }

    // Create download link and trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Authentication endpoints (centralized in shop-svc but accessible here)
  async getCurrentUser(): Promise<any> {
    return this.request('/api/auth/user');
  }

  async logout(allDevices: boolean = false): Promise<void> {
    const endpoint = allDevices ? '/api/auth/logout?allDevices=true' : '/api/auth/logout';
    await this.request<void>(endpoint, { method: 'POST' });
  }

  async testAuth(): Promise<{ status: string; user: any }> {
    return this.request('/api/auth/test');
  }

  // Quick Analyze endpoints
  async triggerQuickAnalyze(shopId: string, images: File[], userText?: string): Promise<TriggerAnalysisResponse> {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error('User not authenticated');
    }

    if (images.length === 0) {
      throw new Error('At least 1 image is required');
    }

    if (images.length > 10) {
      throw new Error('Maximum 10 images allowed');
    }

    const formData = new FormData();
    images.forEach((file) => {
      formData.append('images', file);
    });

    // Append user text if provided
    if (userText && userText.trim()) {
      formData.append('userText', userText.trim());
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/shops/${shopId}/quick-analyze`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();

      // Handle rate limit errors (HTTP 429)
      if (response.status === 429) {
        try {
          const errorBody = JSON.parse(errorText);
          if (errorBody.error === 'rate_limit_exceeded') {
            throw RateLimitError.fromResponse(errorBody);
          }
        } catch (parseError) {
          if (parseError instanceof RateLimitError) {
            throw parseError;
          }
        }
        throw new Error('Rate limit exceeded for Quick Analyze. Please upgrade your plan.');
      }

      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  async getQuickAnalyzeResults(shopId: string, analysisId: string): Promise<QuickAnalyzeResponse> {
    return this.request<QuickAnalyzeResponse>(`/api/v1/shops/${shopId}/quick-analyze/${analysisId}`);
  }

  async convertQuickAnalyzeToSlide(
    shopId: string,
    analysisId: string,
    overrides?: {
      name?: string;
      description?: string;
      categories?: string[];
    }
  ): Promise<Slide> {
    return this.request<Slide>(`/api/v1/shops/${shopId}/quick-analyze/${analysisId}/convert-to-slide`, {
      method: 'POST',
      // Quick Analyze may create an ACQUISITION record from a detected price tag, so it needs the
      // user's local date rather than the server's instant.
      body: JSON.stringify({ ...(overrides || {}), acquisitionDate: localTodayISODate() }),
    });
  }

  // Background task status for a slide
  async getBackgroundTasks(shopId: string, slideId: string): Promise<BackgroundTask[]> {
    return this.request<BackgroundTask[]>(`/api/v1/shops/${shopId}/slides/${slideId}/background-tasks`);
  }

  async analyzeExistingSlide(shopId: string, slideId: string, userText?: string): Promise<TriggerAnalysisResponse> {
    const params = userText?.trim() ? `?userText=${encodeURIComponent(userText.trim())}` : '';
    return this.request<TriggerAnalysisResponse>(`/api/v1/shops/${shopId}/slides/${slideId}/analyze${params}`, {
      method: 'POST',
    });
  }

  // Standalone Marketplace Eval endpoint
  async runStandaloneMarketplaceEval(
    shopId: string,
    images: File[],
    searchQuery?: string,
    imageIndex?: number
  ): Promise<MarketplaceEvalResponse> {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error('User not authenticated');
    }

    if (images.length === 0) {
      throw new Error('At least 1 image is required');
    }

    // Determine which image to send (use imageIndex if provided, otherwise use first image)
    const selectedImageIndex = imageIndex !== undefined && imageIndex !== null ? imageIndex : 0;
    if (selectedImageIndex >= images.length) {
      throw new Error(`Invalid image index: ${selectedImageIndex}`);
    }
    const imageToSend = images[selectedImageIndex];

    const formData = new FormData();
    // Backend expects single 'image' parameter (not 'images')
    formData.append('image', imageToSend);

    // Append search query if provided
    if (searchQuery && searchQuery.trim()) {
      formData.append('searchQuery', searchQuery.trim());
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/shops/${shopId}/marketplace-eval/standalone`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();

      // Handle rate limit errors (HTTP 429)
      if (response.status === 429) {
        try {
          const errorBody = JSON.parse(errorText);
          if (errorBody.error === 'rate_limit_exceeded') {
            throw RateLimitError.fromResponse(errorBody);
          }
        } catch (parseError) {
          if (parseError instanceof RateLimitError) {
            throw parseError;
          }
        }
        throw new Error('Rate limit exceeded for Marketplace Eval. Please upgrade your plan.');
      }

      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  // ============================================================
  // CSV Inventory Import (v1.1.6) — OWNER/ADMIN only
  // ============================================================

  /** Upload + validate a CSV. Returns a preview; nothing is created until confirmed. */
  async uploadImport(shopId: string, file: File): Promise<ImportPreviewResponse> {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error('User not authenticated');
    }
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/api/v1/shops/${shopId}/imports`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let body: any = null;
      try { body = JSON.parse(errorText); } catch { /* not JSON */ }

      if (response.status === 409) {
        throw ImportInProgressError.fromResponse(body || {});
      }
      if (response.status === 429 && body?.error === 'rate_limit_exceeded') {
        throw RateLimitError.fromResponse(body);
      }
      // 400 (invalid CSV) surfaces the server's message
      throw new Error(body?.message || `HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  async getImportStatus(shopId: string, jobId: string): Promise<ImportStatusResponse> {
    return this.request<ImportStatusResponse>(`/api/v1/shops/${shopId}/imports/${jobId}`);
  }

  /**
   * The shop's current live import, or null if it has none. Lets the Import view restore an
   * in-flight import on load instead of the user discovering it as a 409 on their next upload.
   *
   * The server answers 204 (no body) when nothing is in flight, so this cannot go through
   * `request()` — that would call response.json() on an empty body and throw.
   */
  async getActiveImport(shopId: string): Promise<ImportStatusResponse | null> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/v1/shops/${shopId}/imports/active`, {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
    if (response.status === 204) return null;
    if (!response.ok) {
      const errorText = await response.text();
      let body: any = null;
      try { body = JSON.parse(errorText); } catch { /* not JSON */ }
      throw new Error(body?.message || `HTTP ${response.status}: ${errorText || response.statusText}`);
    }
    return response.json();
  }

  async getImportWarnings(shopId: string, jobId: string): Promise<ImportWarning[]> {
    return this.request<ImportWarning[]>(`/api/v1/shops/${shopId}/imports/${jobId}/warnings`);
  }

  /** Confirm a validated import (enqueues stub creation). Returns the now-CONFIRMED status (HTTP 202). */
  async confirmImport(shopId: string, jobId: string): Promise<ImportStatusResponse> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/v1/shops/${shopId}/imports/${jobId}/confirm`, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
    if (!response.ok) {
      const errorText = await response.text();
      let body: any = null;
      try { body = JSON.parse(errorText); } catch { /* not JSON */ }
      throw new Error(body?.message || `HTTP ${response.status}: ${errorText || response.statusText}`);
    }
    return response.json(); // 202 Accepted with the ImportStatusResponse body
  }

  /** The shop's import history, newest first. Only imports that actually created slides appear. */
  async getImportHistory(shopId: string): Promise<ImportHistoryItem[]> {
    return this.request<ImportHistoryItem[]>(`/api/v1/shops/${shopId}/imports`);
  }

  /** How many of an import's slides a cleanup would delete, and how many it would keep. */
  async getDeletableSlides(shopId: string, jobId: string): Promise<ImportDeletionPreview> {
    return this.request<ImportDeletionPreview>(`/api/v1/shops/${shopId}/imports/${jobId}/deletable-slides`);
  }

  /**
   * Request deletion of an import's still-untouched slides. Returns the accepted run state.
   *
   * Raw fetch rather than `request()`: that helper returns `{}` for 202, which would throw away the
   * body this endpoint sends. Same reason `confirmImport` is written this way.
   */
  async deleteImportedSlides(shopId: string, jobId: string): Promise<ImportDeletionStatus> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/v1/shops/${shopId}/imports/${jobId}/delete-slides`, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
    if (!response.ok) {
      const errorText = await response.text();
      let body: any = null;
      try { body = JSON.parse(errorText); } catch { /* not JSON */ }
      throw new Error(body?.message || `HTTP ${response.status}: ${errorText || response.statusText}`);
    }
    return response.json(); // 202 Accepted with the ImportDeletionStatusResponse body
  }

  async getSlideDeletionStatus(shopId: string, jobId: string): Promise<ImportDeletionStatus> {
    return this.request<ImportDeletionStatus>(`/api/v1/shops/${shopId}/imports/${jobId}/delete-slides`);
  }

  async cancelImport(shopId: string, jobId: string): Promise<ImportStatusResponse> {
    return this.request<ImportStatusResponse>(`/api/v1/shops/${shopId}/imports/${jobId}/cancel`, {
      method: 'POST',
    });
  }
}

// Singleton instance
export const slideApiService = new SlideApiService();

// Maintain backward compatibility
export const apiService = slideApiService;